import React, { useState } from 'react';
import { Search, Plus, ArrowLeft, MessageSquare, Package, ShoppingCart, Send, Check, X, Trash2, AlertCircle, CheckCircle2, ChevronRight, MapPin, CalendarCheck, Truck } from 'lucide-react';
import { useTickets, useTags, Ticket as TicketType, Tag, replyToTicket, setTicketStatus, approveReturn, rejectReturn, addTag, deleteTag, attachReturnShipment } from '../store/tickets';
import { addShipment, CARRIER_LOGOS, StatusBadge, Status } from './Spedizioni';
// Lightweight lookup so the ticket sidebar can preview a linked shipment
// without coupling to the heavier admin Spedizioni store (whose IDs differ).
type LinkedShipment = {
  id: string;
  trackingId: string;
  carrier: string;
  status: Status;
  store: string;
  address: string;
  city: string;
  departure: string;
  eta: string;
  colli: number;
};
const LINKED_SHIPMENTS: Record<string, LinkedShipment> = {
  'SH-2840-119': {
    id: 'SH-2840-119',
    trackingId: 'FX4892013',
    carrier: 'FedEx',
    status: 'Anomalia',
    store: 'Store Milano Duomo',
    address: 'Galleria Vittorio Emanuele, Milano',
    city: 'Milano',
    departure: '09 mag 08:12',
    eta: '11 mag',
    colli: 6
  },
  'SH-2840-118': {
    id: 'SH-2840-118',
    trackingId: 'FX9923011',
    carrier: 'FedEx',
    status: 'In consegna',
    store: 'Store Roma Termini',
    address: 'Via del Corso 88, Roma',
    city: 'Roma',
    departure: '08 mag 14:02',
    eta: 'Oggi 16:30',
    colli: 4
  },
  'SH-2840-117': {
    id: 'SH-2840-117',
    trackingId: 'GLS882211',
    carrier: 'GLS',
    status: 'In transito',
    store: 'Store Torino Centro',
    address: 'Via Roma 24, Torino',
    city: 'Torino',
    departure: '09 mag 17:30',
    eta: '12 mag',
    colli: 5
  },
  'SH-2840-116': {
    id: 'SH-2840-116',
    trackingId: 'UPS771203',
    carrier: 'UPS',
    status: 'Consegnata',
    store: 'Store Catania',
    address: 'Via Etnea 102, Catania',
    city: 'Catania',
    departure: '08 mag 10:22',
    eta: 'Consegnata 08 mag',
    colli: 3
  },
  'SH-2840-115': {
    id: 'SH-2840-115',
    trackingId: 'SDA445102',
    carrier: 'SDA',
    status: 'In partenza',
    store: 'Store Napoli',
    address: 'Via Toledo 145, Napoli',
    city: 'Napoli',
    departure: '10 mag 06:00',
    eta: '14 mag',
    colli: 7
  }
};
type Section = 'tutti' | 'da-approvare' | 'categorie';
export function Ticket({
  section,
  selectedId,
  onSelectedIdChange
}: {
  section: Section;
  selectedId: string | null;
  onSelectedIdChange: (id: string | null) => void;
}) {
  const tickets = useTickets();
  const tags = useTags();
  const selected = tickets.find(t => t.id === selectedId) || null;
  if (selected) {
    return <TicketDetail ticket={selected} tags={tags} onBack={() => onSelectedIdChange(null)} data-id="element-3328" />;
  }
  if (section === 'categorie') {
    return <TicketCategories tags={tags} data-id="element-3329" />;
  }
  return <TicketList section={section} tickets={tickets} tags={tags} onSelect={onSelectedIdChange} data-id="element-3330" />;
}
function TicketList({
  section,
  tickets,
  tags,
  onSelect
}: {
  section: Section;
  tickets: TicketType[];
  tags: Tag[];
  onSelect: (id: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('Tutti');
  const filtered = tickets.filter(t => {
    if (section === 'da-approvare') {
      if (t.tagId !== 'reso' || t.returnApproval?.status !== 'pending') return false;
    }
    if (statusFilter !== 'Tutti' && t.status !== statusFilter) return false;
    if (query) {
      const q = query.toLowerCase();
      return t.id.toLowerCase().includes(q) || t.subject.toLowerCase().includes(q) || t.customerName.toLowerCase().includes(q) || t.refId.toLowerCase().includes(q);
    }
    return true;
  });
  return <div className="space-y-6" data-id="element-3331">
      <div className="flex items-end justify-between flex-wrap gap-3" data-id="element-3332">
        <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-3333">
          {section === 'da-approvare' ? 'Resi da approvare' : 'Tutti i ticket'}
        </h1>
      </div>

      <div className="bg-[#181818] border border-[#262626] rounded-xl overflow-hidden" data-id="element-3334">
        <div className="p-4 sm:p-5 border-b border-[#262626] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-3335">
          <div className="flex items-center gap-1 p-1 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F] w-max" data-id="element-3336">
            {['Tutti', 'Aperto', 'In lavorazione', 'In attesa', 'Risolto', 'Chiuso'].map(s => <button key={s} onClick={() => setStatusFilter(s)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${statusFilter === s ? 'bg-[#1F1F1F] text-[#FAFAFA]' : 'text-[#9A9A9A] hover:text-[#FAFAFA]'}`} data-id="element-3337">
                {s}
              </button>)}
          </div>
          <div className="relative w-full sm:w-64" data-id="element-3338">
            <Search className="h-4 w-4 text-[#6B6B6B] absolute left-3 top-1/2 -translate-y-1/2" data-id="element-3339" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca ticket..." className="w-full pl-9 pr-3 py-1.5 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F] text-xs text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:ring-2 focus:ring-[#D4FF3A]/30" data-id="element-3340" />
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-3341">
          <table className="w-full text-sm [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap" data-id="element-3342">
            <thead data-id="element-3343">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B6B6B] border-b border-[#1F1F1F]" data-id="element-3344">
                <th className="px-5 py-3 font-medium" data-id="element-3345">ID</th>
                <th className="px-5 py-3 font-medium" data-id="element-3346">Oggetto</th>
                <th className="px-5 py-3 font-medium" data-id="element-3347">Categoria</th>
                <th className="px-5 py-3 font-medium" data-id="element-3348">Riferimento</th>
                <th className="px-5 py-3 font-medium" data-id="element-3349">Cliente</th>
                <th className="px-5 py-3 font-medium" data-id="element-3350">Ultimo agg.</th>
                <th className="px-5 py-3 font-medium" data-id="element-3351">Stato</th>
              </tr>
            </thead>
            <tbody data-id="element-3352">
              {filtered.map(t => {
              const tag = tags.find(tg => tg.id === t.tagId);
              return <tr key={t.id} onClick={() => onSelect(t.id)} className="border-b border-[#1F1F1F] last:border-0 hover:bg-[#0A0A0A] transition-colors cursor-pointer" data-id="element-3353">
                    <td className="px-5 py-3.5 font-mono text-xs text-[#FAFAFA]" data-id="element-3354">
                      {t.id}
                    </td>
                    <td className="px-5 py-3.5 text-[#FAFAFA] max-w-[200px] truncate" data-id="element-3355">
                      {t.subject}
                    </td>
                    <td className="px-5 py-3.5" data-id="element-3356">
                      {tag && <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border" style={{
                    backgroundColor: `${tag.color}15`,
                    color: tag.color,
                    borderColor: `${tag.color}30`
                  }} data-id="element-3357">
                          {tag.name}
                        </span>}
                    </td>
                    <td className="px-5 py-3.5" data-id="element-3358">
                      <div className="flex items-center gap-1.5 text-xs text-[#9A9A9A]" data-id="element-3359">
                        {t.refType === 'shipment' ? <Package className="h-3.5 w-3.5" data-id="element-3360" /> : <ShoppingCart className="h-3.5 w-3.5" data-id="element-3361" />}
                        <span className="font-mono" data-id="element-3362">{t.refId}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-[#FAFAFA]" data-id="element-3363">
                      {t.customerName}
                    </td>
                    <td className="px-5 py-3.5 text-xs font-mono text-[#9A9A9A]" data-id="element-3364">
                      {t.lastUpdate}
                    </td>
                    <td className="px-5 py-3.5" data-id="element-3365">
                      <span className="text-xs font-medium text-[#FAFAFA]" data-id="element-3366">
                        {t.status}
                      </span>
                    </td>
                  </tr>;
            })}
              {filtered.length === 0 && <tr data-id="element-3367">
                  <td colSpan={7} className="px-5 py-10 text-center text-sm text-[#6B6B6B]" data-id="element-3368">
                    Nessun ticket trovato.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}
function TicketDetail({
  ticket,
  tags,
  onBack
}: {
  ticket: TicketType;
  tags: Tag[];
  onBack: () => void;
}) {
  const [replyText, setReplyText] = useState('');
  const tag = tags.find(t => t.id === ticket.tagId);
  const handleReply = () => {
    if (!replyText.trim()) return;
    replyToTicket(ticket.id, replyText.trim(), 'admin');
    setReplyText('');
  };
  return <div className="space-y-6" data-id="element-3369">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-[#9A9A9A] hover:text-[#FAFAFA] transition-colors" data-id="element-3370">
        <ArrowLeft className="h-4 w-4" data-id="element-3371" />
        <span data-id="element-3372">Torna ai ticket</span>
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 items-start" data-id="element-3373">
        {/* Sidebar */}
        <TicketSidebar ticket={ticket} tag={tag} viewer="admin" data-id="element-3374" />

        {/* Conversation */}
        <div className="bg-[#181818] border border-[#262626] rounded-xl overflow-hidden flex flex-col h-[600px]" data-id="element-3375">
          <div className="p-5 border-b border-[#262626] shrink-0" data-id="element-3376">
            <h2 className="text-base font-semibold text-[#FAFAFA] font-heading truncate" data-id="element-3377">
              {ticket.subject}
            </h2>
            <div className="flex items-center gap-2 text-[11px] font-mono text-[#6B6B6B] mt-1" data-id="element-3378">
              <span data-id="element-3379">{ticket.id}</span>
              <span data-id="element-3380">·</span>
              <span data-id="element-3381">Aperto il {ticket.createdAt}</span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-6 bg-[#0A0A0A]" data-id="element-3382">
            {ticket.messages.map(m => {
            const isAdmin = m.sender === 'admin';
            return <div key={m.id} className={`flex flex-col max-w-[85%] ${isAdmin ? 'ml-auto items-end' : 'mr-auto items-start'}`} data-id="element-3383">
                  <div className="flex items-center gap-2 mb-1.5 px-1" data-id="element-3384">
                    <span className="text-xs font-medium text-[#FAFAFA]" data-id="element-3385">
                      {isAdmin ? 'Supporto' : ticket.customerName}
                    </span>
                    <span className="text-[10px] font-mono text-[#6B6B6B]" data-id="element-3386">
                      {m.timestamp}
                    </span>
                  </div>
                  <div className={`p-3.5 rounded-2xl text-sm ${isAdmin ? 'bg-[#D4FF3A] text-[#0A0A0A] rounded-tr-sm' : 'bg-[#1F1F1F] text-[#FAFAFA] rounded-tl-sm'}`} data-id="element-3387">
                    {m.text}
                  </div>
                </div>;
          })}
          </div>

          <div className="p-4 border-t border-[#262626] bg-[#181818] shrink-0" data-id="element-3388">
            <div className="flex items-end gap-3" data-id="element-3389">
              <textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Scrivi una risposta..." className="flex-1 bg-[#0A0A0A] border border-[#262626] rounded-xl p-3 text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/40 resize-none min-h-[80px]" data-id="element-3390" />
              <button onClick={handleReply} disabled={!replyText.trim()} className="p-3 rounded-xl bg-[#D4FF3A] text-[#0A0A0A] hover:bg-[#c2ed28] transition disabled:opacity-50 disabled:cursor-not-allowed shrink-0" data-id="element-3391">
                <Send className="h-5 w-5" data-id="element-3392" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
// Shared sidebar shown on the left of the ticket detail. Renders the
// metadata (tag, reference, customer, dates) plus admin-only controls
// (status switcher, return approval card when applicable).
export function TicketSidebar({
  ticket,
  tag,
  viewer
}: {
  ticket: TicketType;
  tag: Tag | undefined;
  viewer: 'admin' | 'customer';
}) {
  return <aside className="bg-[#181818] border border-[#262626] rounded-xl overflow-hidden lg:sticky lg:top-4" data-id="element-3393">
      <div className="px-5 py-4 border-b border-[#262626]" data-id="element-3394">
        <h3 className="text-sm font-semibold text-[#FAFAFA] font-heading" data-id="element-3395">
          Dettagli ticket
        </h3>
      </div>
      <div className="p-5 space-y-4" data-id="element-3396">
        <SidebarField label="Categoria" data-id="element-3397">
          {tag ? <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider border" style={{
          backgroundColor: `${tag.color}15`,
          color: tag.color,
          borderColor: `${tag.color}30`
        }} data-id="element-3398">
              {tag.name}
            </span> : <span className="text-sm text-[#9A9A9A]" data-id="element-3399">—</span>}
        </SidebarField>

        <SidebarField label="Stato" data-id="element-3400">
          {viewer === 'admin' ? <select value={ticket.status} onChange={e => setTicketStatus(ticket.id, e.target.value as any)} className="w-full bg-[#0A0A0A] border border-[#262626] text-[#FAFAFA] text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-3401">
              <option value="Aperto" data-id="element-3402">Aperto</option>
              <option value="In lavorazione" data-id="element-3403">In lavorazione</option>
              <option value="In attesa" data-id="element-3404">In attesa</option>
              <option value="Risolto" data-id="element-3405">Risolto</option>
              <option value="Chiuso" data-id="element-3406">Chiuso</option>
            </select> : <span className="text-sm font-medium text-[#FAFAFA]" data-id="element-3407">
              {ticket.status}
            </span>}
        </SidebarField>

        <SidebarField label={ticket.refType === 'shipment' ? 'Spedizione collegata' : 'Ordine collegato'} data-id="element-3408">
          <LinkedShipmentCell ticket={ticket} data-id="element-3409" />
        </SidebarField>

        {viewer === 'admin' && <SidebarField label="Cliente" data-id="element-3410">
            <div className="space-y-1" data-id="element-3411">
              <div className="text-sm font-medium text-[#FAFAFA]" data-id="element-3412">
                {ticket.customerName}
              </div>
              <div className="text-[11px] font-mono text-[#9A9A9A] truncate" data-id="element-3413">
                {ticket.customerEmail}
              </div>
            </div>
          </SidebarField>}

        <div className="grid grid-cols-2 gap-3 pt-1" data-id="element-3414">
          <SidebarField label="Aperto il" data-id="element-3415">
            <span className="text-[11px] font-mono text-[#9A9A9A] leading-tight" data-id="element-3416">
              {ticket.createdAt}
            </span>
          </SidebarField>
          <SidebarField label="Ultimo agg." data-id="element-3417">
            <span className="text-[11px] font-mono text-[#9A9A9A] leading-tight" data-id="element-3418">
              {ticket.lastUpdate}
            </span>
          </SidebarField>
        </div>

        {ticket.tagId === 'reso' && ticket.returnApproval && <ReturnApprovalSidebar ticket={ticket} viewer={viewer} data-id="element-3419" />}
      </div>
    </aside>;
}
function SidebarField({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-3420">
      <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-1.5" data-id="element-3421">
        {label}
      </div>
      {children}
    </div>;
}
function LinkedShipmentCell({
  ticket
}: {
  ticket: TicketType;
}) {
  const [open, setOpen] = useState(false);
  const isShipment = ticket.refType === 'shipment';
  const shipment = isShipment ? LINKED_SHIPMENTS[ticket.refId] : undefined;
  if (!isShipment || !shipment) {
    return <div className="flex items-center gap-2 p-2 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F]" data-id="element-3422">
        {isShipment ? <Package className="h-4 w-4 text-[#D4FF3A] shrink-0" data-id="element-3423" /> : <ShoppingCart className="h-4 w-4 text-[#D4FF3A] shrink-0" data-id="element-3424" />}
        <span className="font-mono text-xs text-[#FAFAFA] truncate" data-id="element-3425">
          {ticket.refId}
        </span>
      </div>;
  }
  return <>
      <button type="button" onClick={() => setOpen(true)} className="w-full flex items-center gap-2.5 p-2 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F] hover:border-[#333] hover:bg-[#111] transition group text-left" data-id="element-3426">
        <div className="h-8 w-10 rounded bg-white flex items-center justify-center shrink-0 p-1" data-id="element-3427">
          <img src={CARRIER_LOGOS[shipment.carrier]} alt={shipment.carrier} className="max-w-full max-h-full object-contain" data-id="element-3428" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-3429">
          <div className="font-mono text-xs text-[#FAFAFA] truncate" data-id="element-3430">
            {shipment.id}
          </div>
          <div className="mt-0.5" data-id="element-3431">
            <StatusBadge status={shipment.status} data-id="element-3432" />
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-[#6B6B6B] group-hover:text-[#FAFAFA] transition shrink-0" data-id="element-3433" />
      </button>
      {open && <LinkedShipmentModal shipment={shipment} onClose={() => setOpen(false)} data-id="element-3434" />}
    </>;
}
function LinkedShipmentModal({
  shipment,
  onClose
}: {
  shipment: LinkedShipment;
  onClose: () => void;
}) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-3435">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-3436" />
      <div className="relative w-full max-w-md bg-[#0A0A0A] border border-[#262626] rounded-xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-3437">
        <div className="px-5 py-4 border-b border-[#262626] flex items-center justify-between shrink-0" data-id="element-3438">
          <div className="flex items-center gap-2 min-w-0" data-id="element-3439">
            <Package className="h-4 w-4 text-[#D4FF3A] shrink-0" data-id="element-3440" />
            <h2 className="text-sm font-semibold text-[#FAFAFA] font-heading truncate" data-id="element-3441">
              Dettaglio spedizione
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#181818] transition shrink-0" aria-label="Chiudi" data-id="element-3442">
            <X className="h-4 w-4" data-id="element-3443" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-5" data-id="element-3444">
          {/* Identity */}
          <div className="flex items-center gap-3" data-id="element-3445">
            <div className="h-12 w-14 rounded-lg bg-white flex items-center justify-center shrink-0 p-2" data-id="element-3446">
              <img src={CARRIER_LOGOS[shipment.carrier]} alt={shipment.carrier} className="max-w-full max-h-full object-contain" data-id="element-3447" />
            </div>
            <div className="min-w-0" data-id="element-3448">
              <div className="font-mono text-base text-[#FAFAFA] font-semibold" data-id="element-3449">
                {shipment.id}
              </div>
              <div className="text-[11px] font-mono text-[#6B6B6B] mt-0.5" data-id="element-3450">
                Tracking {shipment.trackingId}
              </div>
            </div>
          </div>

          {/* Status + ETA */}
          <div className="grid grid-cols-2 gap-3" data-id="element-3451">
            <SidebarField label="Stato" data-id="element-3452">
              <StatusBadge status={shipment.status} data-id="element-3453" />
            </SidebarField>
            <SidebarField label="Consegna prevista" data-id="element-3454">
              <div className="inline-flex items-center gap-1.5 text-sm" data-id="element-3455">
                <CalendarCheck className="h-3.5 w-3.5 text-[#D4FF3A]" data-id="element-3456" />
                <span className="font-mono text-[#FAFAFA]" data-id="element-3457">{shipment.eta}</span>
              </div>
            </SidebarField>
          </div>

          {/* Destination */}
          <SidebarField label="Destinazione" data-id="element-3458">
            <div className="flex items-start gap-2" data-id="element-3459">
              <MapPin className="h-3.5 w-3.5 text-[#6B6B6B] shrink-0 mt-0.5" data-id="element-3460" />
              <div className="min-w-0" data-id="element-3461">
                <div className="text-sm text-[#FAFAFA]" data-id="element-3462">{shipment.store}</div>
                <div className="text-xs text-[#9A9A9A] mt-0.5" data-id="element-3463">
                  {shipment.address}
                </div>
              </div>
            </div>
          </SidebarField>

          {/* Carrier + colli */}
          <div className="grid grid-cols-2 gap-3" data-id="element-3464">
            <SidebarField label="Vettore" data-id="element-3465">
              <div className="inline-flex items-center gap-1.5 text-sm text-[#FAFAFA]" data-id="element-3466">
                <Truck className="h-3.5 w-3.5 text-[#6B6B6B]" data-id="element-3467" />
                {shipment.carrier}
              </div>
            </SidebarField>
            <SidebarField label="Colli" data-id="element-3468">
              <div className="font-mono text-sm text-[#FAFAFA]" data-id="element-3469">
                {shipment.colli}
              </div>
            </SidebarField>
          </div>

          {/* Partenza */}
          <SidebarField label="Partenza" data-id="element-3470">
            <div className="text-xs font-mono text-[#9A9A9A]" data-id="element-3471">
              {shipment.departure}
            </div>
          </SidebarField>
        </div>

        <div className="border-t border-[#262626] px-5 py-3 flex justify-end" data-id="element-3472">
          <button type="button" onClick={onClose} className="px-3.5 py-2 rounded-lg bg-[#181818] border border-[#262626] text-[#FAFAFA] text-sm font-medium hover:border-[#333] transition" data-id="element-3473">
            Chiudi
          </button>
        </div>
      </div>
    </div>;
}
function ReturnApprovalSidebar({
  ticket,
  viewer
}: {
  ticket: TicketType;
  viewer: 'admin' | 'customer';
}) {
  const a = ticket.returnApproval!;
  if (a.status === 'pending') {
    if (viewer === 'admin') {
      return <div className="pt-3 border-t border-[#262626]" data-id="element-3474">
          <ReturnApprovalCard ticket={ticket} compact data-id="element-3475" />
        </div>;
    }
    return <div className="pt-3 border-t border-[#262626]" data-id="element-3476">
        <div className="rounded-lg p-3 bg-[#F5A524]/10 border border-[#F5A524]/30" data-id="element-3477">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#F5A524]" data-id="element-3478">
            <AlertCircle className="h-3.5 w-3.5" data-id="element-3479" />
            In attesa di approvazione
          </div>
          <p className="text-xs text-[#9A9A9A] mt-1.5 leading-relaxed" data-id="element-3480">
            Il supporto sta valutando la tua richiesta di reso.
          </p>
        </div>
      </div>;
  }
  if (a.status === 'approved') {
    return <div className="pt-3 border-t border-[#262626]" data-id="element-3481">
        <div className="rounded-lg p-3 bg-[#D4FF3A]/10 border border-[#D4FF3A]/30" data-id="element-3482">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#D4FF3A]" data-id="element-3483">
            <CheckCircle2 className="h-3.5 w-3.5" data-id="element-3484" />
            Reso approvato
          </div>
          {a.trackingNumber && <div className="mt-2 space-y-1" data-id="element-3485">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-3486">
                Tracking · {a.carrier}
              </div>
              <div className="font-mono text-xs text-[#FAFAFA]" data-id="element-3487">
                {a.trackingNumber}
              </div>
            </div>}
        </div>
      </div>;
  }
  return <div className="pt-3 border-t border-[#262626]" data-id="element-3488">
      <div className="rounded-lg p-3 bg-[#EF4444]/10 border border-[#EF4444]/30" data-id="element-3489">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#EF4444]" data-id="element-3490">
          <X className="h-3.5 w-3.5" data-id="element-3491" />
          Reso rifiutato
        </div>
        {a.reason && <p className="text-xs text-[#9A9A9A] mt-1.5 leading-relaxed" data-id="element-3492">
            {a.reason}
          </p>}
      </div>
    </div>;
}
function ReturnApprovalCard({
  ticket,
  compact = false
}: {
  ticket: TicketType;
  compact?: boolean;
}) {
  const [rejecting, setRejecting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [reason, setReason] = useState('');
  const [tracking, setTracking] = useState('');
  const [carrier, setCarrier] = useState('BRT');
  const handleReject = () => {
    if (!reason.trim()) return;
    rejectReturn(ticket.id, reason.trim());
    replyToTicket(ticket.id, `La richiesta di reso è stata rifiutata. Motivazione: ${reason.trim()}`, 'admin');
    setTicketStatus(ticket.id, 'Chiuso');
  };
  const handleApprove = () => {
    if (!tracking.trim()) return;
    approveReturn(ticket.id, tracking.trim(), carrier);
    // Create shipment
    const now = new Date();
    const itMonth = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'][now.getMonth()];
    const todayLabel = `${String(now.getDate()).padStart(2, '0')} ${itMonth} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const newShipmentId = `SH-${tracking.slice(-6).toUpperCase()}`;
    addShipment({
      id: newShipmentId,
      trackingId: tracking.trim(),
      store: 'Magazzino Resi',
      address: '—',
      city: '—',
      carrier,
      colli: 1,
      weight: '—',
      status: 'In partenza',
      signed: 'none',
      cost: '—',
      departure: todayLabel,
      eta: '—',
      reference: ticket.refId,
      contact: {
        name: ticket.customerName,
        phone: '—',
        email: ticket.customerEmail
      },
      steps: [],
      events: [{
        date: todayLabel,
        label: 'Etichetta generata',
        location: '—',
        completed: true
      }],
      apiData: {
        source: 'brt-live',
        currentPhase: 'In partenza',
        lastStatusDate: todayLabel.split(' ')[0] + ' ' + todayLabel.split(' ')[1],
        lastStatusTime: todayLabel.split(' ')[2],
        delivered: false,
        isReturn: true
      }
    });
    attachReturnShipment(ticket.id, newShipmentId);
    replyToTicket(ticket.id, `Reso approvato. Le abbiamo generato l'etichetta di reso con corriere ${carrier}. Tracking: ${tracking.trim()}`, 'admin');
    setTicketStatus(ticket.id, 'In lavorazione');
  };
  if (rejecting) {
    return <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-xl p-5" data-id="element-3493">
        <h3 className="text-sm font-semibold text-[#EF4444] mb-3" data-id="element-3494">
          Motivazione rifiuto
        </h3>
        <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Inserisci il motivo del rifiuto..." className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg p-3 text-sm text-[#FAFAFA] mb-3 focus:outline-none focus:border-[#EF4444]/50" rows={2} data-id="element-3495" />
        <div className="flex items-center gap-2" data-id="element-3496">
          <button onClick={() => setRejecting(false)} className="px-3 py-1.5 rounded-lg text-sm font-medium text-[#9A9A9A] hover:text-[#FAFAFA] transition" data-id="element-3497">
            Annulla
          </button>
          <button onClick={handleReject} disabled={!reason.trim()} className="px-3 py-1.5 rounded-lg bg-[#EF4444] text-white text-sm font-semibold hover:bg-[#DC2626] transition disabled:opacity-50" data-id="element-3498">
            Conferma rifiuto
          </button>
        </div>
      </div>;
  }
  if (approving) {
    return <div className="bg-[#D4FF3A]/10 border border-[#D4FF3A]/30 rounded-xl p-5" data-id="element-3499">
        <h3 className="text-sm font-semibold text-[#D4FF3A] mb-3" data-id="element-3500">
          Associa spedizione
        </h3>
        <div className="space-y-3 mb-4" data-id="element-3501">
          <div data-id="element-3502">
            <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-1.5" data-id="element-3503">
              Corriere
            </label>
            <select value={carrier} onChange={e => setCarrier(e.target.value)} className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-3504">
              <option value="BRT" data-id="element-3505">BRT</option>
              <option value="FedEx" data-id="element-3506">FedEx</option>
              <option value="GLS" data-id="element-3507">GLS</option>
              <option value="UPS" data-id="element-3508">UPS</option>
              <option value="SDA" data-id="element-3509">SDA</option>
            </select>
          </div>
          <div data-id="element-3510">
            <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-1.5" data-id="element-3511">
              Tracking
            </label>
            <input type="text" value={tracking} onChange={e => setTracking(e.target.value)} placeholder="Inserisci tracking..." className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] font-mono focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-3512" />
          </div>
        </div>
        <div className="flex flex-col gap-2" data-id="element-3513">
          <button onClick={handleApprove} disabled={!tracking.trim()} className="w-full px-3 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition disabled:opacity-50" data-id="element-3514">
            Conferma e crea spedizione
          </button>
          <button onClick={() => setApproving(false)} className="w-full px-3 py-2 rounded-lg text-sm font-medium text-[#9A9A9A] hover:text-[#FAFAFA] transition" data-id="element-3515">
            Annulla
          </button>
        </div>
      </div>;
  }
  if (compact) {
    return <div className="rounded-lg p-3 bg-[#F5A524]/10 border border-[#F5A524]/30 space-y-2.5" data-id="element-3516">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#F5A524]" data-id="element-3517">
          <AlertCircle className="h-3.5 w-3.5" data-id="element-3518" />
          Reso in attesa
        </div>
        <p className="text-xs text-[#9A9A9A] leading-relaxed" data-id="element-3519">
          Decidi se approvare la richiesta di reso del cliente.
        </p>
        <div className="grid grid-cols-2 gap-2" data-id="element-3520">
          <button onClick={() => setRejecting(true)} className="px-2 py-1.5 rounded-md border border-[#EF4444]/30 text-[#EF4444] text-xs font-semibold hover:bg-[#EF4444]/10 transition" data-id="element-3521">
            Rifiuta
          </button>
          <button onClick={() => setApproving(true)} className="px-2 py-1.5 rounded-md bg-[#D4FF3A] text-[#0A0A0A] text-xs font-semibold hover:bg-[#c2ed28] transition" data-id="element-3522">
            Approva
          </button>
        </div>
      </div>;
  }
  return <div className="bg-[#181818] border border-[#262626] rounded-xl p-5 flex items-center justify-between gap-4 flex-wrap" data-id="element-3523">
      <div data-id="element-3524">
        <h3 className="text-base font-semibold text-[#FAFAFA] flex items-center gap-2" data-id="element-3525">
          <AlertCircle className="h-4 w-4 text-[#F5A524]" data-id="element-3526" />
          Richiesta di reso in attesa
        </h3>
        <p className="text-sm text-[#9A9A9A] mt-1" data-id="element-3527">
          Il cliente ha richiesto un reso per{' '}
          {ticket.refType === 'order' ? "l'ordine" : 'la spedizione'}{' '}
          <span className="font-mono text-[#FAFAFA]" data-id="element-3528">{ticket.refId}</span>.
        </p>
      </div>
      <div className="flex items-center gap-2" data-id="element-3529">
        <button onClick={() => setRejecting(true)} className="px-4 py-2 rounded-lg border border-[#EF4444]/30 text-[#EF4444] text-sm font-semibold hover:bg-[#EF4444]/10 transition" data-id="element-3530">
          Rifiuta reso
        </button>
        <button onClick={() => setApproving(true)} className="px-4 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-3531">
          Approva reso
        </button>
      </div>
    </div>;
}
const COLORS = ['#EF4444', '#F5A524', '#D4FF3A', '#10B981', '#5DA9FF', '#8B5CF6', '#EC4899', '#9A9A9A' // Grey
];
function TicketCategories({
  tags
}: {
  tags: Tag[];
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(COLORS[4]);
  const handleAdd = () => {
    if (!newName.trim()) return;
    addTag({
      id: newName.toLowerCase().replace(/\s+/g, '-'),
      name: newName.trim(),
      color: newColor
    });
    setNewName('');
    setIsAdding(false);
  };
  return <div className="space-y-6 max-w-3xl" data-id="element-3532">
      <div className="flex items-end justify-between flex-wrap gap-3" data-id="element-3533">
        <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-3534">
          Categorie Ticket
        </h1>
        <button onClick={() => setIsAdding(true)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-3535">
          <Plus className="h-4 w-4" data-id="element-3536" />
          Nuova categoria
        </button>
      </div>

      <div className="bg-[#181818] border border-[#262626] rounded-xl overflow-hidden" data-id="element-3537">
        <table className="w-full text-sm [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap" data-id="element-3538">
          <thead data-id="element-3539">
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B6B6B] border-b border-[#1F1F1F]" data-id="element-3540">
              <th className="px-5 py-3 font-medium" data-id="element-3541">Nome categoria</th>
              <th className="px-5 py-3 font-medium" data-id="element-3542">Colore</th>
              <th className="px-5 py-3 font-medium w-8" data-id="element-3543" />
            </tr>
          </thead>
          <tbody data-id="element-3544">
            {tags.map(t => <tr key={t.id} className="border-b border-[#1F1F1F] last:border-0" data-id="element-3545">
                <td className="px-5 py-3.5" data-id="element-3546">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border" style={{
                backgroundColor: `${t.color}15`,
                color: t.color,
                borderColor: `${t.color}30`
              }} data-id="element-3547">
                    {t.name}
                  </span>
                </td>
                <td className="px-5 py-3.5" data-id="element-3548">
                  <div className="flex items-center gap-2" data-id="element-3549">
                    <div className="h-4 w-4 rounded-full" style={{
                  backgroundColor: t.color
                }} data-id="element-3550" />
                    <span className="text-xs font-mono text-[#9A9A9A]" data-id="element-3551">
                      {t.color}
                    </span>
                  </div>
                </td>
                <td className="px-5 py-3.5 text-right" data-id="element-3552">
                  {t.fixed ? <span className="text-[10px] text-[#6B6B6B] uppercase tracking-wider font-medium" data-id="element-3553">
                      Non eliminabile
                    </span> : <button onClick={() => deleteTag(t.id)} className="p-1.5 rounded text-[#9A9A9A] hover:text-[#EF4444] hover:bg-[#0A0A0A] transition" data-id="element-3554">
                      <Trash2 className="h-4 w-4" data-id="element-3555" />
                    </button>}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {isAdding && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-3556">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setIsAdding(false)} data-id="element-3557" />
          <div className="relative w-full max-w-md bg-[#0A0A0A] border border-[#262626] rounded-xl overflow-hidden" data-id="element-3558">
            <div className="p-5 border-b border-[#262626] flex items-center justify-between" data-id="element-3559">
              <h3 className="text-base font-semibold text-[#FAFAFA] font-heading" data-id="element-3560">
                Nuova categoria
              </h3>
              <button onClick={() => setIsAdding(false)} className="p-1 rounded-md text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#181818] transition" data-id="element-3561">
                <X className="h-4 w-4" data-id="element-3562" />
              </button>
            </div>
            <div className="p-5 space-y-4" data-id="element-3563">
              <div data-id="element-3564">
                <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-1.5" data-id="element-3565">
                  Nome categoria
                </label>
                <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Es. Reclamo" className="w-full bg-[#181818] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-3566" />
              </div>
              <div data-id="element-3567">
                <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-1.5" data-id="element-3568">
                  Colore
                </label>
                <div className="flex items-center gap-2 flex-wrap" data-id="element-3569">
                  {COLORS.map(c => <button key={c} onClick={() => setNewColor(c)} className={`h-8 w-8 rounded-full flex items-center justify-center transition-transform ${newColor === c ? 'scale-110 ring-2 ring-offset-2 ring-offset-[#0A0A0A]' : 'hover:scale-110'}`} style={{
                backgroundColor: c,
                ringColor: c
              }} data-id="element-3570">
                      {newColor === c && <Check className="h-4 w-4 text-[#0A0A0A]" data-id="element-3571" />}
                    </button>)}
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-[#262626] flex items-center justify-end gap-2 bg-[#181818]" data-id="element-3572">
              <button onClick={() => setIsAdding(false)} className="px-3.5 py-2 rounded-lg text-sm font-medium text-[#9A9A9A] hover:text-[#FAFAFA] transition" data-id="element-3573">
                Annulla
              </button>
              <button onClick={handleAdd} disabled={!newName.trim()} className="px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition disabled:opacity-50" data-id="element-3574">
                Aggiungi
              </button>
            </div>
          </div>
        </div>}
    </div>;
}